/**
 * Who offers, and who else is in the call.
 *
 * SEPARATE FROM lib/sessionLink.js FOR ONE REASON: this file imports nothing.
 * It is a pure function of its arguments, which means a test can load it
 * directly in Node - the same trick e2e/ticket-stage.mjs uses on
 * lib/ticketStage.js - rather than needing a browser, a bundler and a stubbed
 * RTCPeerConnection to reach the one decision whose failure is completely
 * silent.
 *
 * And it is the silent one. A connection that never completes looks like a
 * network problem and gets blamed on one; nobody goes looking for a rule that
 * made both sides offer at once.
 */

/* ------------------------------------------------------- who starts the call
 *
 * WebRTC lets either side offer, and if both do at the same moment the
 * negotiation collides - "glare" - and the connection fails in a way that
 * looks like bad luck. So exactly one of the two has to be the offerer, by a
 * rule both of them can work out for themselves without asking.
 *
 * THE PERSON SHARING OFFERS. That is the user: they are the one whose media
 * the call exists to carry, and making the owner of the media the offerer
 * means the one thing that changes mid-call - a screen share starting or
 * stopping - is changed by the side that is already in charge of the
 * negotiation.
 *
 * When the two roles are the same - two administrators looking at a ticket
 * together - there is no natural answer, so the smaller participant id wins.
 * Arbitrary, and that is the point: both sides compute the same thing from
 * data they both have, so neither has to be told.
 */
export function shouldOffer(me, peer) {
  if (!me || !peer) return false;
  if (me.role !== peer.role) return me.role === 'user';
  return String(me.id) < String(peer.id);
}

/**
 * Everybody else in the call, in an order every page agrees on.
 *
 * A FULL MESH, since Gordon asked for calls "not limited to 2 people". Each
 * pair of people has its own peer connection, and shouldOffer above settles
 * who offers on each one - which is why the rule had to be a property of a
 * PAIR rather than of a call: with three people there are three pairs, and
 * every one of them needs exactly one offerer, worked out independently at
 * both ends.
 *
 * The cost is real and it is accepted: somebody sharing a screen sends it once
 * per other person, so a mesh is for a handful of people, not a room. For a
 * firm where a call is a user, whoever is fixing their machine and perhaps a
 * colleague or two, that is the right trade against running a media server.
 *
 * THE ORDER is only for the screen - the fixer first, since that is who the
 * user is waiting for; then whoever arrived earliest; then by id, so two
 * people who joined in the same instant still come out the same way round on
 * every render rather than swapping tiles.
 *
 * `present` comes from the server, which derives it from a heartbeat rather
 * than from a goodbye - because a closed lid sends no goodbye.
 */
export function peersOf(me, participants) {
  const here = (participants || []).filter((p) => p.present && p.id !== me?.id);
  const order = (p) => (p.role === 'fixer' ? 0 : 1);
  return here.sort((a, b) => order(a) - order(b)
    || new Date(a.joined_at) - new Date(b.joined_at)
    || (String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0));
}

/**
 * "With A, B and C" - the people a call is with, as a sentence.
 *
 * Here rather than in the page because it is a pure function of a list, and
 * the ways a list of names goes wrong as English (one name, two, the comma
 * before the last) are cheaper to check in Node than in a browser.
 */
export function namesOf(people, tag = (p) => (p.role === 'fixer' ? ' (IT)' : '')) {
  const names = (people || []).map((p) => `${p.display_name || 'Somebody'}${tag(p)}`);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
